import React, { useState, useEffect } from 'react';
import {
  Form, Switch, Button, Row, Col, TimePicker, message,
  Space, Typography, Tooltip, Badge, Modal, Popconfirm,
  Table, Alert, Input
} from 'antd';

const { TextArea } = Input;
import { 
  PlusOutlined, DeleteOutlined, EditOutlined, ClockCircleOutlined,
  BankOutlined, CheckCircleOutlined, StopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { useSettingsStore } from '../../shared/stores/useSettingsStore';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

interface CvuConfig {
  name: string;
  number: string;
  holder: string;
  active: boolean;
  startTime: string | null;
  endTime: string | null;
}

interface CvuSettingsProps {
  form: any;
  cvuConfigs: Record<string, CvuConfig>;
  setCvuConfigs: (configs: Record<string, CvuConfig>) => void;
}

interface CvuTableData extends CvuConfig {
  key: string;
  configName: string;
}

interface ActiveCvuResult {
  config: (CvuConfig & { configName: string }) | null;
  colorIndex?: number;
}

interface HourAnalysis {
  hour: number;
  currentActive: (CvuConfig & { configName: string; colorIndex: number }) | null;
  nextActive: (CvuConfig & { configName: string; colorIndex: number }) | null;
  transitionTime: string | null;
  minutesRemaining: number | null;
  hasTransition: boolean;
  isCurrentHour: boolean;
  exactStatus: string;
}

// CVU Color System - Professional distinct colors
const CVU_COLOR_SYSTEM: { bg: string; text: string; border: string; gradient: string }[] = [
  { bg: '#e6f4ff', text: '#1890ff', border: '#91caff', gradient: 'linear-gradient(135deg, #e6f4ff, #bae0ff)' }, // Blue
  { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f', gradient: 'linear-gradient(135deg, #f6ffed, #d9f7be)' }, // Green
  { bg: '#fff2e8', text: '#fa8c16', border: '#ffbb96', gradient: 'linear-gradient(135deg, #fff2e8, #ffd591)' }, // Orange
  { bg: '#f9f0ff', text: '#722ed1', border: '#d3adf7', gradient: 'linear-gradient(135deg, #f9f0ff, #efdbff)' }, // Purple
  { bg: '#fff1f0', text: '#f5222d', border: '#ffa39e', gradient: 'linear-gradient(135deg, #fff1f0, #ffccc7)' }, // Red
  { bg: '#e6fffb', text: '#13c2c2', border: '#87e8de', gradient: 'linear-gradient(135deg, #e6fffb, #b5f5ec)' }, // Cyan
  { bg: '#feffe6', text: '#a0d911', border: '#eaff8f', gradient: 'linear-gradient(135deg, #feffe6, #f4ffb8)' }, // Lime
  { bg: '#fff0f6', text: '#eb2f96', border: '#ffadd2', gradient: 'linear-gradient(135deg, #fff0f6, #ffd6e7)' }, // Magenta
];

// Professional Timeline Container
const TimelineContainer = styled.div`
  background: var(--color-bg-container);
  border-radius: 8px;
  padding: 20px;
  margin: 16px 0;
  border: 1px solid var(--color-border);
`;

const TimelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 2px;
  background: var(--color-border);
  border-radius: 4px;
  overflow: hidden;
  margin: 16px 0;
`;

const HourBlock = styled.div<{ 
  isActive: boolean; 
  isCurrent: boolean; 
  hasTransition: boolean;
  configName?: string;
  colorIndex?: number;
}>`
  background: ${props => {
    if (props.isActive && props.colorIndex !== undefined && props.colorIndex >= 0) {
      const colorSystem = CVU_COLOR_SYSTEM[props.colorIndex % CVU_COLOR_SYSTEM.length];
      if (props.isCurrent) {
        return props.hasTransition ? 
          `linear-gradient(45deg, ${colorSystem.text}, #faad14)` : 
          colorSystem.text;
      }
      return props.hasTransition ? colorSystem.gradient : colorSystem.bg;
    }
    return props.isCurrent ? '#faad14' : 'var(--color-bg-container)';
  }};
  
  color: ${props => {
    if (props.isActive && props.colorIndex !== undefined && props.colorIndex >= 0) {
      const colorSystem = CVU_COLOR_SYSTEM[props.colorIndex % CVU_COLOR_SYSTEM.length];
      return props.isCurrent || props.hasTransition ? '#fff' : colorSystem.text;
    }
    return props.isCurrent ? '#fff' : 'var(--color-text)';
  }};
  
  border: ${props => {
    if (props.isActive && props.colorIndex !== undefined && props.colorIndex >= 0) {
      const colorSystem = CVU_COLOR_SYSTEM[props.colorIndex % CVU_COLOR_SYSTEM.length];
      return `2px solid ${colorSystem.border}`;
    }
    return props.isCurrent ? '2px solid #faad14' : '1px solid var(--color-border)';
  }};
  
  padding: 6px 3px;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  min-height: 45px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  transition: all 0.2s ease;
  
  ${props => props.isCurrent && `
    box-shadow: 0 0 0 2px #faad14;
    z-index: 2;
  `}
  
  ${props => props.hasTransition && `
    position: relative;
    &:after {
      content: '⚡';
      position: absolute;
      top: 2px;
      right: 2px;
      font-size: 8px;
      color: #fff;
      text-shadow: 0 0 2px rgba(0,0,0,0.5);
    }
  `}
  
  &:hover {
    transform: scale(1.08);
    z-index: 3;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

const CurrentIndicator = styled.div<{ color?: string }>`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 10px solid ${props => props.color || '#faad14'};
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendColor = styled.div<{ bg: string; border: string }>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background: ${props => props.bg};
  border: 1px solid ${props => props.border};
`;

const ConfigBadge = styled.div<{ colorIndex: number }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${props => {
    const safeIndex = Math.max(0, props.colorIndex) % CVU_COLOR_SYSTEM.length;
    return CVU_COLOR_SYSTEM[safeIndex].bg;
  }};
  color: ${props => {
    const safeIndex = Math.max(0, props.colorIndex) % CVU_COLOR_SYSTEM.length;
    return CVU_COLOR_SYSTEM[safeIndex].text;
  }};
  border: 1px solid ${props => {
    const safeIndex = Math.max(0, props.colorIndex) % CVU_COLOR_SYSTEM.length;
    return CVU_COLOR_SYSTEM[safeIndex].border;
  }};
  font-size: 12px;
  font-weight: 500;
`;

const SystemControlCard = styled.div`
  background: var(--color-bg-container);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const CvuSettings: React.FC<CvuSettingsProps> = ({ form, cvuConfigs: rawCvuConfigs, setCvuConfigs }) => {
  const [currentTime, setCurrentTime] = useState<dayjs.Dayjs>(dayjs());
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [editingConfig, setEditingConfig] = useState<string | null>(null);
  const [modalForm] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [systemControlLoading, setSystemControlLoading] = useState<boolean>(false);
  const [processedCvuConfigs, setProcessedCvuConfigs] = useState<Record<string, CvuConfig>>({});

  const {
    cvuSystemStatus,
    enableCvuSystem,
    disableCvuSystem,
    getCvuSystemStatus,
    upsertCvuConfig: upsertCvuConfigStore,
    deleteCvuConfig: deleteCvuConfigStore
  } = useSettingsStore();

  // Process CVU configs to convert escaped newlines to actual newlines
  useEffect(() => {
    const processed: Record<string, CvuConfig> = {};

    for (const [key, config] of Object.entries(rawCvuConfigs)) {
      let processedHolder = config.holder;

      if (processedHolder && typeof processedHolder === 'string') {
        // Replace escaped newlines with actual newlines
        processedHolder = processedHolder
          .replace(/\\r\\n/g, '\n')
          .replace(/\\n/g, '\n')
          .replace(/\\r/g, '\n');
      }

      processed[key] = {
        ...config,
        holder: processedHolder
      };
    }


    setProcessedCvuConfigs(processed);
  }, [rawCvuConfigs]);

  // Use processed configs instead of raw
  const cvuConfigs = processedCvuConfigs;

  // Update current time every minute
  useEffect(() => {
    const updateTime = (): void => {
      setCurrentTime(dayjs());
    };
    
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  // Load CVU system status on mount
  useEffect(() => {
    getCvuSystemStatus().catch(console.error);
  }, [getCvuSystemStatus]);

  // Fixed getConfigIndex function to ensure safe indexing
  const getConfigIndex = (configName: string): number => {
    const configNames: string[] = Object.keys(cvuConfigs);
    const index = configNames.indexOf(configName);
    return Math.max(0, index); // Ensure non-negative index
  };

  // Safe function to get color system for a config
  const getColorSystemForConfig = (configName: string) => {
    const colorIndex = getConfigIndex(configName);
    return CVU_COLOR_SYSTEM[colorIndex % CVU_COLOR_SYSTEM.length];
  };

  const isTimeInRange = (timeMinutes: number, startTime: string, endTime: string): boolean => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    const startMinutes: number = startHour * 60 + startMinute;
    const endMinutes: number = endHour * 60 + endMinute;

    // Handle cross-midnight ranges
    if (endMinutes <= startMinutes) {
      return timeMinutes >= startMinutes || timeMinutes <= endMinutes;
    } else {
      return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
    }
  };

  const getActiveConfigForTime = (timeMinutes: number): ActiveCvuResult => {
    for (const [configName, config] of Object.entries(cvuConfigs)) {
      if (!config.active || !config.startTime || !config.endTime) continue;
      
      if (isTimeInRange(timeMinutes, config.startTime, config.endTime)) {
        return { 
          config: { ...config, configName },
          colorIndex: getConfigIndex(configName)
        };
      }
    }
    
    return { config: null };
  };

  const analyzeHour = (hour: number): HourAnalysis => {
    const hourStart = hour * 60;
    const currentMinutes = currentTime.hour() * 60 + currentTime.minute();
    const isCurrentHour = hour === currentTime.hour();
    
    const startActive = getActiveConfigForTime(hourStart);
    
    let transitionTime: string | null = null;
    let nextActive: (CvuConfig & { configName: string; colorIndex: number }) | null = null;
    let hasTransition = false;
    
    for (let minute = 0; minute < 60; minute++) {
      const timeMinutes = hourStart + minute;
      const currentActiveAtTime = getActiveConfigForTime(timeMinutes);
      const nextMinuteActive = getActiveConfigForTime(timeMinutes + 1);
      
      if (currentActiveAtTime.config?.configName !== nextMinuteActive.config?.configName) {
        transitionTime = `${hour.toString().padStart(2, '0')}:${(minute + 1).toString().padStart(2, '0')}`;
        nextActive = nextMinuteActive.config ? {
          ...nextMinuteActive.config,
          colorIndex: nextMinuteActive.colorIndex!
        } : null;
        hasTransition = true;
        break;
      }
    }
    
    let minutesRemaining: number | null = null;
    if (isCurrentHour && startActive.config) {
      const [endHour, endMinute] = startActive.config.endTime!.split(':').map(Number);
      const endMinutes = endHour * 60 + endMinute;
      
      if (endMinutes <= currentMinutes) {
        minutesRemaining = (24 * 60) - currentMinutes + endMinutes;
      } else {
        minutesRemaining = endMinutes - currentMinutes;
      }
    }
    
    let exactStatus: string;
    if (isCurrentHour) {
      const currentMinute = currentTime.minute();
      const timeDisplay = `${hour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
      
      if (startActive.config) {
        if (hasTransition && transitionTime) {
          exactStatus = `${timeDisplay} - Active: ${startActive.config.name} → Changes to ${nextActive ? nextActive.name : 'None'} at ${transitionTime}`;
        } else if (minutesRemaining !== null) {
          const hours = Math.floor(minutesRemaining / 60);
          const mins = minutesRemaining % 60;
          const remainingDisplay = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
          exactStatus = `${timeDisplay} - Active: ${startActive.config.name} (${remainingDisplay} remaining)`;
        } else {
          exactStatus = `${timeDisplay} - Active: ${startActive.config.name}`;
        }
      } else {
        if (hasTransition && nextActive && transitionTime) {
          exactStatus = `${timeDisplay} - No CVU → ${nextActive.name} starts at ${transitionTime}`;
        } else {
          exactStatus = `${timeDisplay} - No CVU active`;
        }
      }
    } else {
      if (hasTransition && transitionTime) {
        exactStatus = `${hour.toString().padStart(2, '0')}:00 - ${startActive.config ? startActive.config.name : 'No CVU'} → ${nextActive ? nextActive.name : 'None'} at ${transitionTime}`;
      } else {
        exactStatus = `${hour.toString().padStart(2, '0')}:00 - ${startActive.config ? `Active: ${startActive.config.name}` : 'No CVU active'}`;
      }
    }
    
    return {
      hour,
      currentActive: startActive.config ? {
        ...startActive.config,
        colorIndex: startActive.colorIndex!
      } : null,
      nextActive,
      transitionTime,
      minutesRemaining,
      hasTransition,
      isCurrentHour,
      exactStatus
    };
  };

  const getCurrentActiveCvu = (): ActiveCvuResult => {
    const currentMinutes: number = currentTime.hour() * 60 + currentTime.minute();
    return getActiveConfigForTime(currentMinutes);
  };

  const hasTimeOverlap = (configs: Record<string, CvuConfig>, currentConfigName: string, newConfig: CvuConfig): boolean => {
    if (!newConfig.active || !newConfig.startTime || !newConfig.endTime) return false;

    for (const [configName, config] of Object.entries(configs)) {
      if (configName === currentConfigName || !config.active) continue;
      if (!config.startTime || !config.endTime) continue;

      const overlap: boolean = timeRangesOverlap(
        newConfig.startTime, newConfig.endTime,
        config.startTime, config.endTime
      );

      if (overlap) return true;
    }
    return false;
  };

  const timeRangesOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
    const [s1h, s1m] = start1.split(':').map(Number);
    const [e1h, e1m] = end1.split(':').map(Number);
    const [s2h, s2m] = start2.split(':').map(Number);
    const [e2h, e2m] = end2.split(':').map(Number);

    const s1: number = s1h * 60 + s1m;
    const e1: number = e1h * 60 + e1m;
    const s2: number = s2h * 60 + s2m;
    const e2: number = e2h * 60 + e2m;

    const range1CrossesMidnight: boolean = e1 <= s1;
    const range2CrossesMidnight: boolean = e2 <= s2;

    if (range1CrossesMidnight && range2CrossesMidnight) {
      return true;
    } else if (range1CrossesMidnight) {
      return (s2 >= s1) || (e2 <= e1);
    } else if (range2CrossesMidnight) {
      return (s1 >= s2) || (e1 <= e2);
    } else {
      return (s1 < e2) && (s2 < e1);
    }
  };

  const handleSystemToggle = async (): Promise<void> => {
    setSystemControlLoading(true);
    
    try {
      if (cvuSystemStatus.enabled) {
        await disableCvuSystem();
        message.success('CVU system disabled successfully');
      } else {
        await enableCvuSystem();
        message.success('CVU system enabled successfully');
      }
    } catch (error) {
      console.error('Error toggling CVU system:', error);
      message.error(`Error ${cvuSystemStatus.enabled ? 'disabling' : 'enabling'} CVU system`);
    } finally {
      setSystemControlLoading(false);
    }
  };

  const handleAddConfig = (): void => {
    setEditingConfig(null);
    modalForm.resetFields();
    setIsModalVisible(true);
  };

  const handleEditConfig = (configName: string): void => {
    const config: CvuConfig = cvuConfigs[configName];
    if (!config) {
      message.error('Configuration not found');
      return;
    }



    setEditingConfig(configName);
    modalForm.setFieldsValue({
      name: config.name,
      number: config.number,
      holder: config.holder,
      active: config.active,
      startTime: config.startTime ? dayjs(config.startTime, 'HH:mm') : null,
      endTime: config.endTime ? dayjs(config.endTime, 'HH:mm') : null,
    });

    setIsModalVisible(true);
  };

  const handleDeleteConfig = async (configName: string): Promise<void> => {
    try {
      // Delete from backend automatically
      await deleteCvuConfigStore(configName);

      // Update local state
      const newConfigs: Record<string, CvuConfig> = { ...cvuConfigs };
      delete newConfigs[configName];
      setCvuConfigs(newConfigs);

      const formValue = form.getFieldValue('cvuConfigs') || {};
      delete formValue[configName];
      form.setFieldValue('cvuConfigs', formValue);

      message.success('CVU configuration deleted successfully');
    } catch (error) {
      console.error('Error deleting CVU config:', error);
      message.error('Failed to delete CVU configuration');
    }
  };

  const handleSaveConfig = async (): Promise<void> => {
    try {
      setLoading(true);
      const values = await modalForm.validateFields();


      const configData: CvuConfig = {
        name: values.name,
        number: values.number,
        holder: values.holder,
        active: values.active || false,
        startTime: values.startTime ? values.startTime.format('HH:mm') : null,
        endTime: values.endTime ? values.endTime.format('HH:mm') : null,
      };


      let finalConfigName: string;

      if (editingConfig) {
        finalConfigName = editingConfig;

        if (hasTimeOverlap(cvuConfigs, editingConfig, configData)) {
          message.error('Time range overlaps with another active CVU');
          return;
        }
      } else {
        finalConfigName = values.name.replace(/\s+/g, '_').toLowerCase() || `cvu_${Date.now()}`;

        if (cvuConfigs[finalConfigName]) {
          finalConfigName = `${finalConfigName}_${Date.now()}`;
        }

        if (hasTimeOverlap(cvuConfigs, finalConfigName, configData)) {
          message.error('Time range overlaps with another active CVU');
          return;
        }
      }

      // Save to backend automatically
      console.log('[CvuSettings] Saving config:', { finalConfigName, configData, valuesFromForm: values });

      await upsertCvuConfigStore(finalConfigName, configData);


      // Update local state
      const newConfigs: Record<string, CvuConfig> = { ...cvuConfigs };
      newConfigs[finalConfigName] = configData;


      setCvuConfigs(newConfigs);

      const formValue = form.getFieldValue('cvuConfigs') || {};
      formValue[finalConfigName] = configData;
      form.setFieldValue('cvuConfigs', formValue);

      setIsModalVisible(false);
      setEditingConfig(null);
      message.success(`CVU ${editingConfig ? 'updated' : 'created'} and saved successfully`);

    } catch (error) {
      console.error('Error saving CVU config:', error);
      message.error('Failed to save CVU configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = (): void => {
    setIsModalVisible(false);
    setEditingConfig(null);
    modalForm.resetFields();
  };

  // Prepare table data
  const tableData: CvuTableData[] = Object.entries(cvuConfigs).map(([configName, config]) => ({
    key: configName,
    configName,
    ...config,
  }));

  // Table columns definition with proper error handling
  const columns: ColumnsType<CvuTableData> = [
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 80,
      render: (active: boolean) => (
        <Badge 
          status={active ? "success" : "default"} 
          text={active ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (name: string, record: CvuTableData) => {
        const colorSystem = getColorSystemForConfig(record.configName);
        
        return (
          <Space>
            <div 
              style={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%',
                background: colorSystem.text,
                border: `2px solid ${colorSystem.border}`
              }}
            />
            <Text strong>{name || record.configName}</Text>
          </Space>
        );
      },
    },
    {
      title: 'CVU Number',
      dataIndex: 'number',
      key: 'number',
      width: 200,
      render: (number: string) => (
        <Text copyable={{ text: number }}>{number || '-'}</Text>
      ),
    },
    {
      title: 'Holder',
      dataIndex: 'holder',
      key: 'holder',
      width: 180,
      render: (holder: string) => (
        <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {holder || '-'}
        </Text>
      ),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      width: 150,
      render: (_, record: CvuTableData) => {
        if (record.startTime && record.endTime) {
          return (
            <Space>
              <ClockCircleOutlined style={{ color: '#1890ff' }} />
              <Text>{record.startTime} - {record.endTime}</Text>
            </Space>
          );
        }
        return <Text type="secondary">No schedule</Text>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record: CvuTableData) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditConfig(record.configName)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete CVU Configuration"
            description="Are you sure you want to delete this CVU configuration?"
            onConfirm={() => handleDeleteConfig(record.configName)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const currentActive = getCurrentActiveCvu();

  const renderTimeline = (): JSX.Element => {
    const activeConfigs = Object.entries(cvuConfigs).filter(([_, config]) => config.active);
    
    return (
      <TimelineContainer>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <ClockCircleOutlined style={{ color: 'var(--color-primary)' }} />
              <Text strong style={{ color: 'var(--color-text)' }}>24-Hour CVU Schedule</Text>
            </Space>
          </Col>
          <Col>
            <Text style={{ color: 'var(--color-text-secondary)' }}>
              Current: {currentTime.format('HH:mm')} - {
                currentActive.config ? (
                  <ConfigBadge colorIndex={currentActive.colorIndex || 0}>
                    Sending: {currentActive.config.name}
                  </ConfigBadge>
                ) : 'No CVU Active'
              }
            </Text>
          </Col>
        </Row>

        <TimelineGrid>
          {Array.from({ length: 24 }, (_, hour: number) => {
            const analysis = analyzeHour(hour);
            const currentColor = analysis.currentActive ? 
              CVU_COLOR_SYSTEM[(analysis.currentActive.colorIndex || 0) % CVU_COLOR_SYSTEM.length].text : '#faad14';
            
            return (
              <Tooltip
                key={hour}
                title={analysis.exactStatus}
                overlayStyle={{ maxWidth: '400px' }}
              >
                <HourBlock
                  isActive={!!analysis.currentActive}
                  isCurrent={analysis.isCurrentHour}
                  hasTransition={analysis.hasTransition}
                  configName={analysis.currentActive?.configName}
                  colorIndex={analysis.currentActive?.colorIndex}
                >
                  {analysis.isCurrentHour && <CurrentIndicator color={currentColor} />}
                  <div>{hour.toString().padStart(2, '0')}</div>
                  {analysis.currentActive && (
                    <div style={{ fontSize: '9px', marginTop: 1 }}>
                      {analysis.currentActive.name.slice(0, 5)}
                    </div>
                  )}
                  {analysis.hasTransition && analysis.transitionTime && (
                    <div style={{ fontSize: '7px', marginTop: 1 }}>
                      →{analysis.transitionTime.slice(3)}
                    </div>
                  )}
                </HourBlock>
              </Tooltip>
            );
          })}
        </TimelineGrid>

        {activeConfigs.length > 0 && (
          <>
            <LegendContainer>
              <LegendItem>
                <LegendColor bg="#fff7e6" border="#faad14" />
                <Text style={{ fontSize: 12, color: 'var(--color-text)' }}>Current Hour</Text>
              </LegendItem>
              {activeConfigs.map(([configName, config]) => {
                const colorSystem = getColorSystemForConfig(configName);
                return (
                  <LegendItem key={configName}>
                    <LegendColor bg={colorSystem.bg} border={colorSystem.border} />
                    <Text style={{ fontSize: 12, color: 'var(--color-text)' }}>
                      {config.name} ({config.startTime}-{config.endTime})
                    </Text>
                  </LegendItem>
                );
              })}
              <LegendItem>
                <div style={{ fontSize: 12, color: 'var(--color-text)' }}>⚡ = Transition</div>
              </LegendItem>
            </LegendContainer>
          </>
        )}
      </TimelineContainer>
    );
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        {/* CVU System Control */}
        <Col xs={24}>
          <SystemControlCard>
            <Row justify="space-between" align="middle">
              <Col>
                <Space direction="vertical" size={4}>
                  <Title level={5} style={{ margin: 0 }}>
                    <BankOutlined style={{ marginRight: 8 }} />
                    CVU System Control
                  </Title>
                  <Text type="secondary">
                    {cvuSystemStatus.enabled 
                      ? 'CVU system is currently active and processing requests' 
                      : 'CVU system is disabled - all CVU requests will be rejected'
                    }
                  </Text>
                </Space>
              </Col>
              <Col>
                <Space>
                  {cvuSystemStatus.enabled ? (
                    <Badge status="success" text="Enabled" />
                  ) : (
                    <Badge status="error" text="Disabled" />
                  )}
                  <Button
                    type={cvuSystemStatus.enabled ? "default" : "primary"}
                    danger={cvuSystemStatus.enabled}
                    icon={cvuSystemStatus.enabled ? <StopOutlined /> : <CheckCircleOutlined />}
                    onClick={handleSystemToggle}
                    loading={systemControlLoading}
                  >
                    {cvuSystemStatus.enabled ? 'Disable System' : 'Enable System'}
                  </Button>
                </Space>
              </Col>
            </Row>
          </SystemControlCard>
        </Col>

        {/* System Status Alert */}
        {!cvuSystemStatus.enabled && (
          <Col xs={24}>
            <Alert
              message="CVU System Disabled"
              description="The CVU system is currently disabled. All requests to /cvu/active endpoint will return false until you enable it again."
              type="warning"
              showIcon
              closable={false}
            />
          </Col>
        )}

        {/* Timeline */}
        {cvuSystemStatus.enabled && (
          <Col xs={24}>
            {renderTimeline()}
          </Col>
        )}

        {/* CVU Configurations Table */}
        <Col xs={24}>
          <div style={{
            background: 'var(--color-bg-container)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Space>
                  <BankOutlined />
                  <Title level={5} style={{ margin: 0 }}>CVU Configurations</Title>
                  <Badge count={Object.keys(cvuConfigs).length} />
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddConfig}
                >
                  Add CVU
                </Button>
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              size="middle"
              scroll={{ x: 800 }}
              locale={{
                emptyText: (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    color: 'var(--color-text-secondary)'
                  }}>
                    <BankOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                    <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>
                      No CVU Configurations
                    </div>
                    <Text type="secondary">
                      Add your first CVU to start time-based scheduling
                    </Text>
                  </div>
                )
              }}
            />
          </div>
        </Col>
      </Row>

      {/* Add/Edit CVU Modal */}
      <StyledModal
        title={editingConfig ? 'Edit CVU' : 'Add CVU'}
        open={isModalVisible}
        onOk={handleSaveConfig}
        onCancel={handleModalClose}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={modalForm}
          layout="vertical"
          initialValues={{ active: true }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  { required: true, message: 'Enter name' },
                  { min: 2, max: 20, message: 'Name must be 2-20 characters' },
                  { pattern: /^[a-zA-Z0-9\s]+$/, message: 'Only letters, numbers and spaces allowed' }
                ]}
              >
                <Input placeholder="CVU Name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="active"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="number"
            label="CVU/CBU Number"
            rules={[
              { required: true, message: 'Enter CVU number' },
              { pattern: /^\d{22}$/, message: 'Must be 22 digits' }
            ]}
          >
            <Input placeholder="22-digit CVU/CBU" maxLength={22} />
          </Form.Item>

          <Form.Item
            name="holder"
            label="Holder Name"
            rules={[{ required: true, message: 'Enter holder name' }]}
          >
            <TextArea
              placeholder="Account holder name&#10;You can use multiple lines"
              autoSize={{ minRows: 2, maxRows: 6 }}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="startTime"
                label="Start Time"
                rules={[{ required: true, message: 'Select start time' }]}
              >
                <TimePicker
                  format="HH:mm"
                  placeholder="Start time"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="endTime"
                label="End Time"
                rules={[{ required: true, message: 'Select end time' }]}
              >
                <TimePicker
                  format="HH:mm"
                  placeholder="End time"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="Cross-midnight ranges are supported (e.g., 22:00 - 06:00). All times are in Buenos Aires timezone."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Form>
      </StyledModal>
    </div>
  );
};

export default CvuSettings;